import { AppBar, Box, IconButton, Toolbar, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import LanguageIcon from '@mui/icons-material/Language'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { CompanyLogoSmall } from '@/common/components/AppToolBar/Components/CompanyLogo/CompanyLogoSmall'
import routes from '@/routes'
import ImageToSvgConverter from '@/common/components/ImageToSvgConverter/ImageToSvgConverter'
import { useMobileNavBarLogic } from './useMobileNavBarLogic'
import { userIsAuthorize } from './MobileNavBarHelpers'
import { LanguageMenu } from './LanguageMenu'
import { NavigationMenu } from './NavigationMenu'
import {
  toolbarStyles,
  logoContainerStyles,
  logoImageStyles,
  rightButtonsContainerStyles,
  languageIconButtonStyles,
  pageTitleButtonStyles,
  pageTitleTextStyles,
  navbarRootStyles,
  spacerStyles,
  languageIconStyles,
  expandIconStyles,
} from './MobileNavBarStyles'
import CustomPalette from '@/theme/CustomPalette'

const NavbarRoot = styled(AppBar)(() => ({
  backgroundColor: CustomPalette.SecondaryCloud,
  boxShadow: 'none',
}))

export const MobileNavBar = (): JSX.Element => {
  const { t } = useTranslation('general')
  const {
    user,
    tenant,
    location,
    languages,
    menuAnchor,
    languageMenuAnchor,
    currentLanguage,
    showLanguageInMenu,
    handleDrawerToggle,
    handleMenuClose,
    handleNavigate,
    handleLanguageMenuOpen,
    handleLanguageMenuClose,
    handleLanguageChange,
    signOut,
  } = useMobileNavBarLogic()

  if (!user) {
    return <></>
  }

  const myAccountRoutes = routes.filter(
    ({ authorize, menu }) =>
      menu && userIsAuthorize(authorize, user.roles) && ['my_courses_nav', 'my_certificates_nav'].includes(menu.label),
  )

  const currentRoute = myAccountRoutes.find(({ path }) => location.pathname.includes(path))
  const currentTitle = currentRoute?.menu?.label ? t(currentRoute.menu.label) : t('myAccount')

  return (
    <>
      <NavbarRoot position="fixed" sx={navbarRootStyles}>
        <Toolbar disableGutters sx={toolbarStyles}>
          <Box sx={logoContainerStyles}>
            {tenant?.logo_url ? (
              <Box sx={logoImageStyles}>
                <ImageToSvgConverter imageString={tenant.logo_url} />
              </Box>
            ) : (
              <CompanyLogoSmall />
            )}
          </Box>

          <Box sx={spacerStyles} />

          <Box sx={rightButtonsContainerStyles}>
            {!showLanguageInMenu && (
              <IconButton onClick={handleLanguageMenuOpen} sx={languageIconButtonStyles}>
                <LanguageIcon sx={languageIconStyles} />
              </IconButton>
            )}

            <IconButton onClick={handleDrawerToggle} sx={pageTitleButtonStyles}>
              <Typography sx={pageTitleTextStyles}>{currentTitle}</Typography>
              <ExpandMoreIcon sx={expandIconStyles} />
            </IconButton>
          </Box>
        </Toolbar>
      </NavbarRoot>

      <LanguageMenu
        anchorEl={languageMenuAnchor}
        languages={languages}
        currentLanguage={currentLanguage}
        onClose={handleLanguageMenuClose}
        onLanguageChange={handleLanguageChange}
      />

      <NavigationMenu
        anchorEl={menuAnchor}
        routes={myAccountRoutes}
        currentPath={location.pathname}
        onClose={handleMenuClose}
        onNavigate={handleNavigate}
        onLogout={signOut}
        showLanguageInMenu={showLanguageInMenu}
        languages={languages}
        currentLanguage={currentLanguage}
        onLanguageChange={handleLanguageChange}
      />
    </>
  )
}
